"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { AlertCircleIcon, Loader2Icon } from "lucide-react";
import React, { useActionState, useId, useState } from "react";
import { toast } from "sonner";
import { useTrackEvent } from "@/components/analytics/events";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ColorPicker, DEFAULT_COLORS } from "@/components/ui/color-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isDemoMode } from "@/config";
import { useRouter } from "@/lib/navigation";
import { useRefreshWorkspaceSidebar } from "../../_contexts/workspace-sidebar-data-context";
import { createProject } from "../../action";

type Props = {
  workspaceId: string;
  trigger: React.ReactNode;
  workspaceUsedColors?: string[];
};

export default function CreateIWProjectDialog({
  workspaceId,
  trigger,
  workspaceUsedColors,
}: Props) {
  const trackEvent = useTrackEvent();
  const { i18n } = useLingui();
  const router = useRouter();
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();
  const title = i18n._(msg`Create Index Worker project`);
  const description = i18n._(
    msg`Using the URL Inspection API of Search Console, we check the index rate and crawl rate of the URL Groups within the site.`,
  );

  const [open, setOpen] = useState(false);

  const [state, action, pending] = useActionState<
    {
      errors: string[];
      projectId?: string;
    },
    FormData
  >(
    async (_, formData) => {
      const name = formData.get("name");
      const color = formData.get("color");
      if (typeof name !== "string") {
        return { errors: ["Invalid form data"] };
      }
      if (typeof color !== "string" || !color) {
        return { errors: ["Color selection is required"] };
      }
      const result = await createProject(workspaceId, name, "iw", color, "");

      // プロジェクトが正常に作成された場合
      if (result.projectId) {
        trackEvent({
          eventName: "iw_project_created",
          payload: { product_area: "iw" },
        });

        // プロジェクトページへ遷移
        // IWは特別な設定をせずに使用開始できるので一覧画面へ直接遷移
        const projectPath = `/${encodeURIComponent(workspaceId)}/iw/${encodeURIComponent(result.projectId)}`;
        router.push(projectPath);
        router.refresh();

        // 成功メッセージを表示
        toast.success(i18n._(msg`Index Worker project created`));

        // ダイアログを閉じる
        setOpen(false);
        refreshWorkspaceSidebar("project-mutation");
      }

      return result;
    },
    {
      errors: [],
    },
  );

  const id = useId();

  const getRandomDefaultColor = () => {
    return DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)];
  };

  return (
    <>
      {React.cloneElement(
        trigger as React.ReactElement<{
          onClick?: () => void;
          disabled?: boolean;
        }>,
        {
          onClick: () => setOpen(true),
        },
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <form action={action}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor={`${id}-name`}>
                  <Trans>Project name</Trans>
                </Label>
                <Input id={`${id}-name`} name="name" required />
              </div>
              <div className="flex flex-col gap-2">
                <Label>
                  <Trans>Project color</Trans>
                </Label>
                <ColorPicker
                  name="color"
                  defaultValue={getRandomDefaultColor()}
                  usedColors={workspaceUsedColors}
                  required
                />
              </div>
            </div>
            {state.errors.length > 0 && (
              <Alert className="mt-4" variant="destructive">
                <AlertCircleIcon className="h-4 w-4" />
                <AlertTitle>
                  <Trans>Error</Trans>
                </AlertTitle>
                <AlertDescription>
                  <ul>
                    {state.errors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter className="mt-4">
              <Button
                type="submit"
                disabled={pending || isDemoMode}
                className="w-20"
              >
                {pending ? (
                  <Loader2Icon className="h-4 w-4 animate-spin" />
                ) : (
                  <Trans>Create</Trans>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
